import { EventType } from '../../triggers/domain';
import { ConversionLog } from '../domain';

export interface ConversionSearchDTO {
  query?: string;
  page: number;
  from?: string;
  to?: string;
  payout_status?: PayoutStatus;
  conversion_name?: string;
  referrer_amount_min?: number;
  referrer_amount_max?: number;
  is_fraud?: boolean;
}

export interface ExportConversionsDTO {
  query?: string;
  from?: string;
  to?: string;
  conversion_name?: string;
  payout_status?: PayoutStatus;
  is_fraud?: boolean;
}

export interface ConversionLogsResponse {
  results: ConversionLog[];
  next_page: number | null;
  count: number;
}

export interface SearchConversionsResponse {
  results: ConversionLog[];
}

export interface FraudSearchDTO {
  page: number;
  from: string;
  to: string;
}

export enum AttributionStatus {
  SendingTransaction = 'sending_transaction',
  PendingPipeline = 'pending_pipeline',
  PendingTransaction = 'pending_transaction',
  PendingConfirmation = 'pending_confirmation',
  Confirmed = 'confirmed',
  ConfirmedNotPaid = 'confirmed_not_paid',
  Stalled = 'stalled',
  Failed = 'failed',
  PendingApproval = 'pending_approval',
  Rejected = 'rejected',
}

export enum PayoutStatus {
  SendingTransaction = 'sending_transaction',
  PendingApproval = 'pending_approval',
  PendingTransaction = 'pending_transaction',
  PendingConfirmation = 'pending_confirmation',
  Confirmed = 'confirmed',
  Failed = 'failed',
  Rejected = 'rejected',
  Deferred = 'deferred',
}

export interface FraudSearchResultDTO {
  id: string;
  date: string;
  payout_status: PayoutStatus;
  end_user_address: string;
  affiliate_address: string;
  end_user_amount: string;
  affiliate_amount: string;
  total_amount: string;
}

export interface ConversionDetailsResponse {
  events: ConversionEventDTO[];
  payout_tier_name: string;
}

export interface ConversionEventDTO {
  type: EventType;
  date: string;
  metadata: Record<string, any>;
  args: object;
  name: string;
  user_address: string;
  evaluated_enduser_address: string;
  evaluated_volume: string;
  evaluated_revenue: string;
  evaluated_currency_address: string;
  trigger_name: string;
}

export interface EventSearchDTO {
  query?: string;
  name?: string;
  type?: string;
  page: number;
  from?: string;
  to?: string;
}

export interface ExportEventsDTO {
  from_date?: string;
  to_date?: string;
  query?: string;
  name?: string;
  type?: string;
}

export interface EventLogsResponse {
  events: EventLogDTO[];
  next_page: number | null;
  count: number;
}

export interface EventLogDTO {
  id: string;
  date: string;
  name: string;
  type: string;
  user_address: string;
  user_identifier: string;
  identifier_type: string;
  event_args: object;
  contract_address?: string;
  transaction_hash?: string;
  chain_id?: number;
  transaction_value?: string;
  referrer?: string;
  source?: string;
}

export interface EventSearchResultResponse {
  trigger_executions: {
    id: string;
    name: string;
    date: string;
    status: string;
    status_details: string;
  }[];
}

export interface UploadEventDto {
  args: {
    value?: {
      amount: string;
      currency: {
        address?: string;
        chain_id?: number;
        name?: string;
      };
    };

    revenue?: {
      amount: string;
      currency: {
        address?: string;
        chain_id?: number;
        name?: string;
      };
    };
  };
  name: string;
  user_address: string;
  timestamp: number;
  dedup_id: string | null;
}

export interface UploadEventDtoWithDedupId extends UploadEventDto {
  dedup_id: string;
}

export interface EventImportRequest {
  file_path: string;
}

export interface EventImport {
  id: string;
  file_name: string;
  status: EventImportStatus;
  errors?: string;
  date: string;
  created_at: string;
  updated_at: string;
}

export enum EventImportStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

export interface PayoutPoolLogsDTO {
  page?: number;
  page_size?: number;
}

export enum PayoutPoolStatus {
  Calculating = 'calculating',
  Calculated = 'calculated',
  PayoutsCreated = 'payouts-created',
  Stopped = 'stopped',
  Failed = 'failed',
}

export interface PayoutPoolLogsResponse {
  results: PayoutPoolListItemResponse[];
  total_count: number;
  next_page: number | null;
}

export interface PayoutPoolListItemResponse {
  id: string;
  payout_date: string;
  from: string;
  to: string;
  conversion_name: string;
  status: string;
  total_amount: string | null;
  amount_source: string | null;
  payout_term_pool_amount: string;
  payout_term_pool_amount_currency_chain_id: number | null;
  payout_term_pool_amount_currency_address: string | null;
  status_details: string | null;
}

export interface PayoutPoolPayoutResponse {
  user_identifier: string;
  value: string;
  payout_amount: string;
  payout_amount_currency_address?: string | null;
  payout_amount_currency_chain_id?: number | null;
  payout_status: PayoutStatus;
  share: number;
  tier_name?: string | null;
}

export interface PayoutPoolPayoutsResponse {
  results: PayoutPoolPayoutResponse[];
  total_count: number;
  next_page: number | null;
}

export interface PayoutPoolDetailsResponse {
  id: string;
  date: string;
  conversion_name: string;
  status: PayoutPoolStatus;
  total_amount: string | null;
  amount_source: string | null;
  payout_term_pool_amount: string;
  payout_term_pool_amount_currency_chain_id: number | null;
  payout_term_pool_amount_currency_address: string | null;
  status_details: string | null;
}

export interface EventResponse {
  id: string;
  name: string;
  type: string;
  event_args: Record<string, unknown>;
  trigger_name: string;
  user_address: string;
}

export interface PayoutPoolItemResponse {
  id: string;
  date: string;
  amount: string;
  user_id: string;
  user_address: string;
  conversion_name: string;
  events: {
    id: string;
    name: string;
    type: string;
    date: string;
    event_args: Record<string, unknown>;
    trigger_name: string;
    user_address: string;
  }[];
}

export interface PayoutPoolItemsResponse {
  results: PayoutPoolItemResponse[];
  total_count: number;
  next_page: number | null;
}
